
import React from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, AlertCircle } from "lucide-react";
import { useAdminAnalytics } from "@/hooks/useAdminAnalytics";
import { AnalyticsSummaryCards } from "./AnalyticsSummaryCards";
import UserMetricsChart from "../dashboard/UserMetricsChart";
import CountryDistributionChart from "../dashboard/CountryDistributionChart";
import LanguageDistributionChart from "../dashboard/LanguageDistributionChart";
import TherapistConnectionsTable from "../dashboard/TherapistConnectionsTable";
import SessionAnalytics from "./SessionAnalytics";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";

const AdminAnalytics = () => {
  const { data: analyticsData, isLoading, isError, error, refetch } = useAdminAnalytics();

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-[#3BAFDA]" />
        <p className="ml-3 text-[#5A636E]">Loading analytics data...</p>
      </div>
    );
  }

  if (isError || !analyticsData) {
    return (
      <div className="p-8">
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error loading analytics data</AlertTitle>
          <AlertDescription>
            {error instanceof Error ? error.message : "Unable to fetch analytics data. Please try again."}
          </AlertDescription>
        </Alert>
        <Button onClick={() => refetch()} variant="outline">
          Retry
        </Button>
      </div>
    );
  }

  const userMetrics = [
    { name: "Total Users", value: analyticsData.userCount },
    { name: "Clients", value: analyticsData.clientCount },
    { name: "Therapists", value: analyticsData.therapistCount },
    { name: "Admins", value: analyticsData.adminCount },
    { name: "Unassigned", value: analyticsData.unassignedCount },
    { name: "Journal Entries", value: analyticsData.journalCount },
    { name: "Active Connections", value: analyticsData.connectionRequests }
  ];

  return (
    <div className="space-y-6">
      <AnalyticsSummaryCards data={analyticsData} />

      <Tabs defaultValue="users" className="space-y-4">
        <TabsList>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="geography">Geography</TabsTrigger>
          <TabsTrigger value="connections">Connections</TabsTrigger>
          <TabsTrigger value="engagement">Engagement</TabsTrigger>
        </TabsList>
        
        <TabsContent value="users" className="space-y-4">
          <Card className="p-4">
            <UserMetricsChart userMetrics={userMetrics} />
          </Card>
        </TabsContent>
        
        <TabsContent value="geography" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card className="p-4">
              <CardHeader>
                <CardTitle>Country Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <CountryDistributionChart countryDistribution={analyticsData.countryDistribution} />
              </CardContent>
            </Card>
            
            <Card className="p-4">
              <CardHeader>
                <CardTitle>Language Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <LanguageDistributionChart languageDistribution={analyticsData.languageDistribution} />
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        <TabsContent value="connections" className="space-y-4">
          <Card className="p-4">
            <CardHeader>
              <CardTitle>Therapist Connections</CardTitle>
            </CardHeader>
            <CardContent>
              <TherapistConnectionsTable therapistsWithConnections={analyticsData.therapistsWithConnections} />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="engagement" className="space-y-4">
          {(analyticsData.dailyActiveUsers && analyticsData.dailyActiveUsers.length > 0) ? (
            <SessionAnalytics
              dailyActiveUsers={analyticsData.dailyActiveUsers || []}
              totalSessions={analyticsData.totalSessions || 0}
              avgSessionDuration={analyticsData.avgSessionDuration || 0}
              totalPageViews={analyticsData.totalPageViews || 0}
            />
          ) : (
            <Alert className="mb-6">
              <AlertTitle>Limited Analytics Data Available</AlertTitle>
              <AlertDescription>
                Enhanced usage analytics could not be loaded. Basic system metrics are still available.
              </AlertDescription>
            </Alert>
          )}

          {analyticsData.mostViewedPages && analyticsData.mostViewedPages.length > 0 && (
            <Card className="p-4">
              <CardHeader>
                <CardTitle>Most Viewed Pages</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {analyticsData.mostViewedPages.map((page) => (
                    <div key={page.page_path} className="flex justify-between items-center">
                      <span className="text-sm font-medium">{page.page_path}</span>
                      <span className="text-sm text-muted-foreground">{page.view_count} views</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminAnalytics;
